import React, { useEffect } from 'react';

/**
 * ModalShell — consistent, APK-safe modal container.
 *
 * Fixes the recurring mobile problem where modals used
 * `fixed inset-0 flex items-center` with no scroll and no safe-area
 * handling: on Android (Capacitor APK) tall modals and the on-screen
 * keyboard pushed action buttons under the status bar / gesture nav,
 * making them unreachable.
 *
 * The overlay scrolls, honours the notch/gesture insets via env(safe-area-*),
 * and the inner card caps its height so its own content scrolls when needed.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose        Called on backdrop click / Escape. Omit to make non-dismissable.
 * @param {'center'|'sheet'} variant 'center' = centered dialog; 'sheet' = bottom sheet on mobile, centered on desktop.
 * @param {string} maxWidth         Tailwind max-width class (default 'max-w-md').
 * @param {string} className        Extra classes for the inner card.
 * @param {string} panelStyle       Optional inline styles merged onto the inner card.
 */
const ModalShell = ({
  isOpen = true,
  onClose,
  variant = 'center',
  maxWidth = 'max-w-md',
  className = '',
  children,
  zIndexClass = 'z-[100]',
}) => {
  // Lock body scroll while open so the background doesn't scroll behind the modal.
  useEffect(() => {
    if (!isOpen) return;
    // <html> is the document scroll container (body uses overflow-x: clip),
    // so lock the root element to stop the page scrolling behind the modal.
    const root = document.documentElement;
    const prevHtml = root.style.overflow;
    const prevBody = document.body.style.overflow;
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      root.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isSheet = variant === 'sheet';

  return (
    <div
      className={`fixed inset-0 ${zIndexClass} flex justify-center overflow-y-auto bg-black/80 backdrop-blur-md px-3 sm:px-4 ${
        isSheet ? 'items-end sm:items-center' : 'items-start sm:items-center'
      }`}
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      }}
      onClick={onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
    >
      <div
        className={`relative w-full ${maxWidth} my-auto overflow-y-auto bg-[#0a0a0f] border border-white/10 shadow-2xl ${
          isSheet ? 'rounded-t-3xl sm:rounded-3xl' : 'rounded-2xl'
        } ${className}`}
        style={{
          maxHeight: 'calc(100dvh - env(safe-area-inset-top,0px) - env(safe-area-inset-bottom,0px) - 2rem)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

export default ModalShell;
